import React from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';
import { MdLocalOffer, MdPercent, MdPersonAdd, MdRepeat, MdStars, MdHourglassBottom, MdShoppingCart, MdLocalShipping } from 'react-icons/md';
import TabbedReport, { pill, TwoLine, Dash, shareBar } from './TabbedReport';
import { FONT, inr, fmtDate } from './reportShared';

const money = (key, label, bold) => ({ key, label, numeric: true, bold, text: (r) => r[key] ?? '', cell: (r) => (r[key] === null || r[key] === undefined ? <Dash /> : inr(r[key])), print: (r) => (r[key] === null || r[key] === undefined ? '-' : inr(r[key])) });
const num = (key, label, bold) => ({ key, label, numeric: true, bold, text: (r) => r[key] ?? '' });
const promoPctChip = (v) => {
  if (v === null || v === undefined) return <Dash />;
  const [bg, fg] = v <= 10 ? ['#dcfce7', '#166534'] : v <= 20 ? ['#fef3c7', '#92400e'] : ['#fee2e2', '#b91c1c'];
  return <Chip size="small" label={`${v}%`} sx={pill(bg, fg)} />;
};

const tabs = [
  {
    key: 'monthly',
    label: 'Monthly Trend',
    columns: [
      { key: 'month', label: 'Month', bold: true, text: (r) => r.month },
      num('orders', 'Orders', true),
      num('firstOrders', 'First Orders'),
      num('repeatOrders', 'Repeat Orders'),
      money('gross', 'Gross Sales (MRP)'),
      money('productDiscount', 'Product Discounts'),
      money('coupon', 'Coupons'),
      money('rewards', 'Rewards Used'),
      money('promoCost', 'Total Promo Cost', true),
      { key: 'promoPct', label: 'Promo % of Sales', center: true, text: (r) => (r.promoPct === null ? '' : `${r.promoPct}%`), cell: (r) => promoPctChip(r.promoPct) },
      num('freeDelivery', 'Free Delivery Orders'),
      money('net', 'Net Collected', true),
    ],
  },
  {
    key: 'discounts',
    label: 'Promotion Levers',
    columns: [
      { key: 'lever', label: 'Lever', bold: true, text: (r) => r.lever, cell: (r) => <TwoLine top={r.lever} bottom={r.note} />, extra: (r) => ({ Note: r.note }) },
      money('cost', 'Cost', true),
      { key: 'sharePct', label: '% of Promo Cost', text: (r) => (r.sharePct === null ? '' : `${r.sharePct}%`), cell: (r) => (r.sharePct === null ? <Dash /> : shareBar(r.sharePct, 'linear-gradient(90deg,#ec4899,#db2777)')) },
      { key: 'pctOfSales', label: '% of Gross Sales', center: true, text: (r) => (r.pctOfSales === null ? '' : `${r.pctOfSales}%`), cell: (r) => promoPctChip(r.pctOfSales) },
      num('orders', 'Orders Using It'),
      { key: 'ordersPct', label: '% of Orders', text: (r) => `${r.ordersPct}%`, cell: (r) => shareBar(r.ordersPct) },
    ],
  },
  {
    key: 'rewards',
    label: 'Reward Points',
    defaultSort: 'balance_desc',
    sorts: [['balance_desc', 'Highest balance'], ['expiring_desc', 'Most expiring soon']],
    rowAlert: (r) => r.expiringSoon > 0,
    columns: [
      { key: 'customer', label: 'Customer', text: (r) => r.customer, cell: (r) => <TwoLine top={r.customer} bottom={r.phone} />, extra: (r) => ({ Phone: r.phone }) },
      num('earned', 'Points Earned'),
      num('redeemed', 'Redeemed'),
      { key: 'usagePct', label: 'Used %', text: (r) => `${r.usagePct}%`, cell: (r) => shareBar(r.usagePct, 'linear-gradient(90deg,#10b981,#059669)') },
      num('balance', 'Balance', true),
      { key: 'expiringSoon', label: 'Expiring in 30 Days', numeric: true, text: (r) => r.expiringSoon, cell: (r) => (r.expiringSoon ? <Chip size="small" label={r.expiringSoon} sx={pill('#fee2e2', '#b91c1c')} /> : <Dash />) },
      { key: 'nextExpiry', label: 'Next Expiry', text: (r) => (r.nextExpiry ? fmtDate(r.nextExpiry) : ''), cell: (r) => (r.nextExpiry ? fmtDate(r.nextExpiry) : <Dash />) },
      { key: 'lastEarned', label: 'Last Earned', text: (r) => (r.lastEarned ? fmtDate(r.lastEarned) : '') },
    ],
  },
  {
    key: 'carts',
    label: 'Abandoned Carts',
    defaultSort: 'value_desc',
    sorts: [['value_desc', 'Cart value: high to low'], ['recent', 'Most recently abandoned']],
    emptyText: 'No abandoned carts right now.',
    columns: [
      { key: 'customer', label: 'Customer', text: (r) => r.customer, cell: (r) => <TwoLine top={r.customer} bottom={[r.phone, r.email].filter(Boolean).join(' · ')} />, extra: (r) => ({ Phone: r.phone, Email: r.email }) },
      num('items', 'Items'),
      money('value', 'Cart Value', true),
      { key: 'updatedAt', label: 'Last Updated', text: (r) => fmtDate(r.updatedAt) },
      { key: 'idleDays', label: 'Abandoned For', center: true, text: (r) => r.idleDays, cell: (r) => <Chip size="small" label={`${r.idleDays} days`} sx={r.idleDays <= 3 ? pill('#dcfce7', '#166534') : r.idleDays <= 14 ? pill('#fef3c7', '#92400e') : pill('#f1f5f9', '#64748b')} /> },
      { key: 'channels', label: 'Can Reach Via', text: (r) => r.channels, cell: (r) => (r.channels ? r.channels.split(', ').map((c) => <Chip key={c} size="small" label={c} sx={{ ...pill('#e0e7ff', '#3730a3'), mr: 0.5 }} />) : <Chip size="small" label="Not opted in" sx={pill('#f1f5f9', '#64748b')} />) },
    ],
  },
];

const OptInPanel = ({ stats }) => {
  const o = stats.optIn || {};
  const items = [
    ['WhatsApp', o.whatsapp, '#16a34a'],
    ['Email', o.email, '#2563eb'],
    ['Push notifications', o.push, '#7c3aed'],
  ];
  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', mb: 3, border: '1px solid #e2e8f0' }}>
      <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a', mb: 1.5 }}>Marketable audience (of {(o.total || 0).toLocaleString('en-IN')} customers)</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
        {items.map(([label, n, color]) => {
          const pct = o.total ? Math.round(((n || 0) / o.total) * 100) : 0;
          return (
            <Box key={label}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, fontFamily: FONT }}>{label}</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 800, fontFamily: FONT, color }}>{(n || 0).toLocaleString('en-IN')} · {pct}%</Typography>
              </Box>
              <Box sx={{ height: 10, borderRadius: 5, background: '#e2e8f0', overflow: 'hidden' }}>
                <Box sx={{ width: `${pct}%`, height: '100%', background: color }} />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

const MarketingReport = () => (
  <TabbedReport
    route="reports/marketing-report"
    title="Marketing Report"
    subtitle="What your promotions cost, whether they bring in new buyers, the rewards you owe, and carts to win back"
    filePrefix="Marketing"
    tabs={tabs}
    searchLabel="Search customer (rewards / carts)"
    filters={[{ key: 'minCart', label: 'Min cart value', tabs: ['carts'], options: [{ value: '500', label: '₹500+' }, { value: '1000', label: '₹1,000+' }, { value: '2500', label: '₹2,500+' }] }]}
    cards={(s, { goTo, isActive }) => [
      { label: 'Promotion Cost', value: inr(s.promoCost), icon: MdLocalOffer, gradient: 'linear-gradient(135deg,#ec4899,#db2777)', sub: `Discounts ${inr(s.productDiscount)} · coupons ${inr(s.coupon)} · rewards ${inr(s.rewardsRedeemed)}`, onClick: () => goTo('discounts'), active: isActive('discounts') },
      { label: 'Promo % of Sales', value: s.promoPct === null || s.promoPct === undefined ? '—' : `${s.promoPct}%`, icon: MdPercent, gradient: 'linear-gradient(135deg,#f59e0b,#ea580c)', sub: `Of ${inr(s.gross)} gross sales`, onClick: () => goTo('monthly'), active: isActive('monthly') },
      { label: 'New Buyers', value: (s.firstOrders || 0).toLocaleString('en-IN'), icon: MdPersonAdd, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)', sub: s.costPerNewBuyer === null || s.costPerNewBuyer === undefined ? `${s.signups || 0} signups` : `${inr(s.costPerNewBuyer)} promo cost each · ${s.signups || 0} signups` },
      { label: 'Repeat Orders', value: s.repeatPct === null || s.repeatPct === undefined ? '—' : `${s.repeatPct}%`, icon: MdRepeat, gradient: 'linear-gradient(135deg,#10b981,#059669)', sub: `${inr(s.repeatRevenue)} from returning customers` },
      { label: 'Free Delivery Orders', value: (s.freeDeliveryOrders || 0).toLocaleString('en-IN'), icon: MdLocalShipping, gradient: 'linear-gradient(135deg,#14b8a6,#0d9488)', sub: `${s.freeGiftUnits || 0} free gifts given · coupons on ${s.couponOrdersPct ?? 0}% of orders` },
      { label: 'Reward Points Owed', value: (s.pointsBalance || 0).toLocaleString('en-IN'), icon: MdStars, gradient: 'linear-gradient(135deg,#eab308,#ca8a04)', sub: `${(s.pointsRedeemed || 0).toLocaleString('en-IN')} of ${(s.pointsEarned || 0).toLocaleString('en-IN')} earned redeemed`, onClick: () => goTo('rewards'), active: isActive('rewards') },
      { label: 'Points Expiring (30d)', value: (s.expiring30 || 0).toLocaleString('en-IN'), icon: MdHourglassBottom, gradient: 'linear-gradient(135deg,#f43f5e,#dc2626)', sub: `${(s.pointsExpired || 0).toLocaleString('en-IN')} already expired unused`, onClick: () => goTo('rewards', { sortBy: 'expiring_desc' }), active: isActive('rewards', { sortBy: 'expiring_desc' }) },
      { label: 'Abandoned Carts', value: inr(s.abandonedValue), icon: MdShoppingCart, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', sub: `${s.abandonedCarts || 0} carts to win back`, onClick: () => goTo('carts'), active: isActive('carts') },
    ]}
    panel={(s) => <OptInPanel stats={s} />}
  />
);

export default MarketingReport;
